import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { oneMoreGoal, pointsFor } from "../utils/scorePoints";
import { shortTeamName } from "./ui/MatchRow";

export default function AnalystDesk({ fixtures, predMap, logoFor }) {
  const [selectedId, setSelectedId] = useState(fixtures[0]?.id ?? null);

  useEffect(() => {
    if (!fixtures.some((m) => m.id === selectedId)) {
      setSelectedId(fixtures[0]?.id ?? null);
    }
  }, [fixtures, selectedId]);

  const match = fixtures.find((m) => m.id === selectedId) || fixtures[0];
  if (!match) return null;

  const pick = predMap[match.id];
  const hasPick = pick && pick.predicted_home_score != null && pick.predicted_away_score != null;
  const status = (match.status || "").toLowerCase();
  const live = status === "live" && match.home_score != null && match.away_score != null;
  const finished = match.home_score != null && match.away_score != null && status === "finished";
  const swing = hasPick && live
    ? oneMoreGoal(pick.predicted_home_score, pick.predicted_away_score, match.home_score, match.away_score)
    : null;
  const scored =
    hasPick && finished
      ? pick.points_awarded ?? pointsFor(pick.predicted_home_score, pick.predicted_away_score, match.home_score, match.away_score)
      : null;

  return (
    <div className="lg:grid lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-6 lg:items-start">
      <div
        role="listbox"
        aria-label="Matches"
        className="max-h-[70vh] overflow-y-auto rounded-xl border border-line bg-white shadow-sm"
      >
        {fixtures.slice(0, 40).map((m) => {
          const active = m.id === match.id;
          const p = predMap[m.id];
          const score =
            m.home_score != null && m.away_score != null ? `${m.home_score}–${m.away_score}` : "v";
          return (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => setSelectedId(m.id)}
              className={`flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2.5 text-left last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${
                active ? "bg-brand-soft" : "hover:bg-gray-50"
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-ink">
                  {shortTeamName(m.home_team)} – {shortTeamName(m.away_team)}
                </span>
                <span className="block text-[12px] text-muted tabular-nums">
                  {p ? `You ${p.predicted_home_score}–${p.predicted_away_score}` : "No pick"}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-ink">{score}</span>
            </button>
          );
        })}
      </div>

      <article className="mt-4 rounded-xl border border-line bg-white shadow-sm lg:mt-0 lg:sticky lg:top-20">
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {logoFor(match.home_team, match.home_crest_url) && (
              <img src={logoFor(match.home_team, match.home_crest_url)} alt="" className="h-8 w-8 object-contain" />
            )}
            <h2 className="truncate text-[17px] font-semibold text-ink">
              {match.home_team} v {match.away_team}
            </h2>
            {logoFor(match.away_team, match.away_crest_url) && (
              <img src={logoFor(match.away_team, match.away_crest_url)} alt="" className="h-8 w-8 object-contain" />
            )}
          </div>
          <p className="shrink-0 text-meta font-semibold uppercase tracking-wide text-muted">{status || "scheduled"}</p>
        </div>
        <dl className="grid grid-cols-3 divide-x divide-line">
          <div className="px-4 py-4">
            <dt className="text-[12px] text-muted">Score</dt>
            <dd className="mt-1 text-[22px] font-bold tabular-nums text-ink">
              {match.home_score != null && match.away_score != null
                ? `${match.home_score}–${match.away_score}`
                : "–"}
            </dd>
          </div>
          <div className="px-4 py-4">
            <dt className="text-[12px] text-muted">Your pick</dt>
            <dd className="mt-1 text-[22px] font-bold tabular-nums text-ink">
              {hasPick ? `${pick.predicted_home_score}–${pick.predicted_away_score}` : "–"}
            </dd>
          </div>
          <div className="px-4 py-4">
            <dt className="text-[12px] text-muted">Points</dt>
            <dd className="mt-1 text-[22px] font-bold tabular-nums text-ink">{scored != null ? scored : swing ? swing.now : "–"}</dd>
          </div>
        </dl>
        {swing && (
          <p className="border-t border-line px-5 py-3 text-sm text-muted">
            Now {swing.now}. A {shortTeamName(match.home_team)} goal makes it {swing.homeGoal}. A{" "}
            {shortTeamName(match.away_team)} goal makes it {swing.awayGoal}.
          </p>
        )}
        <div className="border-t border-line px-5 py-3">
          <Link to={`/predictions?competition=${match.competition || ""}`} className="text-sm font-semibold text-brand">
            Open in Predict
          </Link>
        </div>
      </article>
    </div>
  );
}
